import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { teamViewField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/teamViewField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { TeamViewFieldSkeleton } from './TeamViewFieldSkeleton';
import { useHydratedTeam } from './useHydratedTeam';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-team-view-field.update-action',
		defaultMessage: 'Set team to {fieldValue}',
		description: 'Label for the change action in the jira team view field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.jira-team-view-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the jira team view field',
	},
});

export type Props = {
	cloudId: string;
	queryRef: teamViewField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
	onInvalidAction?: () => void;
};

export const TeamViewField = ({
	cloudId,
	queryRef,
	action,
	registerTooltipProvider,
	onInvalidAction,
}: Props): React.JSX.Element | null => {
	di(useHydratedTeam);

	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment teamViewField_updateWorkItemAllFieldTypesActionRenderer on JiraTeamViewField {
				fieldId
				name
				selectedTeam {
					jiraSuppliedName
				}
			}
		`,
		queryRef,
	);

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	// TODO: Case when we're unassigning teams - is it null or empty string, or what with this schema?
	const suggestedTeamId = fieldValue ? String(fieldValue) : undefined;
	const isFieldRenderable = Boolean(data?.fieldId === fieldKey && data?.name);
	const isUpdatingAction = Boolean(fieldValue);

	const hydratedData = useHydratedTeam(suggestedTeamId, cloudId, true, onInvalidAction);

	const {
		teamId: hydratedTeamId,
		displayName: hydratedTeamName,
		isLoading,
		error,
	} = hydratedData
		? {
				teamId: hydratedData.team?.teamId,
				displayName: hydratedData.team?.displayName,
				isLoading: hydratedData.isLoading,
				error: hydratedData.error,
			}
		: { teamId: null, displayName: null, isLoading: false, error: null };

	const isNotFound = !hydratedTeamId && !isLoading && !!suggestedTeamId;

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				fieldName={data?.name}
				current={data?.selectedTeam?.jiraSuppliedName}
				suggested={hydratedTeamName}
			/>
		),
		[fieldReasoning, data?.name, data?.selectedTeam?.jiraSuppliedName, hydratedTeamName],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable || error || isNotFound) {
		return null;
	}

	if (isLoading) {
		return <TeamViewFieldSkeleton />;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.jira-team-view-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateAction : messages.clearAction, {
				currentTeam: data?.selectedTeam?.jiraSuppliedName,
				fieldValue: hydratedTeamName,
				fieldName: data?.name?.toLowerCase(),
			})}
		</Text>
	);
};
